import { useState } from 'react';
import { COULEURS_MESSAGE, changeStyle, lireStyles, teinte } from '../lib/styles-messages';
import { tr } from '../lib/i18n';
import { C } from '../theme/palette';
import { PleinEcran } from './ui/PleinEcran.jsx';

/**
 * Le rond « Couleur » d'un message, à côté de « ✨ Corriger » : touché, il
 * ouvre en bas de l'écran les couleurs et « En gras ». Toucher à côté les
 * referme. Le texte de la case et le document
 * prennent aussitôt la couleur choisie.
 *
 * @param {object} props propriétés
 * @param {string} props.styles styles des deux messages du document
 * @param {'haut'|'bas'} props.quel message coloré
 * @param {(styles: string) => void} props.onChange reçoit les styles changés
 * @returns {import('react').ReactElement} le rond et, ouvert, ses couleurs
 */
export function CouleurMessage({ styles, quel, onChange }) {
  const [ouvert, setOuvert] = useState(false);
  const { couleur, gras } = lireStyles(styles)[quel];
  const choisit = (style) => onChange(changeStyle(styles, quel, style));
  return (
    <span style={{ display: 'inline-block' }}>
      <button
        type="button"
        aria-expanded={ouvert}
        onClick={() => setOuvert((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 9.5,
          fontWeight: 800,
          color: C.teal,
          cursor: 'pointer',
          background: C.tealL,
          border: 'none',
          padding: '3px 10px 3px 4px',
          borderRadius: 7,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: teinte(couleur),
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px #CBD5E1',
          }}
        />
        {tr('Couleur')}
      </button>
      {ouvert && (
        // Les couleurs s'ouvrent en bas de l'écran, au-dessus de tout : posées
        // sous le bouton, la carte du formulaire les coupait.
        <PleinEcran>
          <div
            onClick={() => setOuvert(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9000,
              background: 'rgba(15,23,42,.28)',
            }}
          />
          <div
            role="dialog"
            aria-label={tr('Couleur du message')}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9001,
              maxWidth: 480,
              margin: '0 auto',
              background: C.white,
              borderRadius: '18px 18px 0 0',
              padding: '16px 18px calc(18px + env(safe-area-inset-bottom))',
              boxShadow: '0 -8px 24px rgba(15,23,42,.18)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: 12 }}>
              {tr('Couleur du message')}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 10,
                justifyItems: 'center',
              }}
            >
              {COULEURS_MESSAGE.map((c) => {
                const choisie = (couleur || 'noir') === c.cle;
                return (
                  <button
                    key={c.cle}
                    type="button"
                    aria-label={tr(c.nom)}
                    aria-pressed={choisie}
                    onClick={() => {
                      choisit({ couleur: c.cle });
                      setOuvert(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: c.hex,
                        boxShadow: choisie ? '0 0 0 3px #fff, 0 0 0 5px ' + C.navy : 'none',
                      }}
                    />
                    <span style={{ fontSize: 9.5, color: C.grey }}>{tr(c.nom)}</span>
                  </button>
                );
              })}
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: 16,
                fontSize: 13,
                fontWeight: 700,
                color: C.ink,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={gras}
                onChange={(e) => choisit({ gras: e.target.checked })}
                style={{ width: 20, height: 20, accentColor: C.green }}
              />
              {tr('En gras (plus visible)')}
            </label>
          </div>
        </PleinEcran>
      )}
    </span>
  );
}
